import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, LinearProgress, Stack, Typography } from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { apiErrorMessage, launchApi } from '../api';
import { launchColors } from '../launchTheme';
import { ProductImportPreview } from '../types';

interface Props {
  open: boolean;
  onClose: () => void;
  onImported: (message: string) => void;
}

const Line: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <Box>
    <Typography sx={{ fontSize: 13, fontWeight: 700, color: launchColors.navy }}>{label}</Typography>
    <Typography component="div" sx={{ fontSize: 13 }}>{children}</Typography>
  </Box>
);

const ImportDialog: React.FC<Props> = ({ open, onClose, onImported }) => {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<ProductImportPreview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setFile(null);
    setPreview(null);
    setError(null);
  }, [open]);

  const choose = async (next: File | null) => {
    setFile(next);
    setPreview(null);
    setError(null);
    if (!next) return;
    setBusy(true);
    try {
      setPreview(await launchApi.previewProductInfoImport(next));
    } catch (requestError) {
      setError(apiErrorMessage(requestError, 'Filen kunde inte läsas.'));
    } finally {
      setBusy(false);
    }
  };

  const mode: 'all' | 'missing' = preview && preview.existing > 0 ? 'missing' : 'all';
  const toAdd = preview ? preview.total - preview.alreadyThere : 0;

  const run = async () => {
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      const result = await launchApi.importProductInfo(file, mode);
      onImported(`${result.imported} produkter lästes in.${result.skipped ? ` ${result.skipped} fanns redan.` : ''}`);
    } catch (requestError) {
      setError(apiErrorMessage(requestError, 'Importen misslyckades.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="md">
      <DialogTitle>Läs in Excel</DialogTitle>
      <DialogContent>
        <Stack spacing={1.5}>
          <Typography sx={{ fontSize: 13 }}>
            Välj filen Produktinformation Dabas. De synliga bladen läses in. Inget sparas förrän du väljer Läs in.
          </Typography>
          <Box>
            <Button component="label" variant="outlined" startIcon={<UploadFileIcon />} disabled={busy}>
              Välj fil
              <input
                hidden
                type="file"
                accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                aria-label="Excel-fil"
                onChange={(event) => { void choose(event.target.files?.[0] ?? null); event.target.value = ''; }}
              />
            </Button>
            {file && <Typography component="span" sx={{ ml: 1, fontSize: 13 }}>{file.name}</Typography>}
          </Box>
          {busy && <LinearProgress />}
          {error && <Alert severity="error">{error}</Alert>}
          {preview && (
            <Stack spacing={1.25}>
              {preview.existing > 0 && (
                <Alert severity="info">
                  Det finns redan {preview.existing} produkter. Bara de som saknas läggs till ({toAdd} st). Befintliga ändras inte.
                </Alert>
              )}
              {(preview.ignoredSheets ?? []).map((name) => (
                <Alert key={name} severity="info">Bladet {name} läses in i Produktinfo Alkohol.</Alert>
              ))}
              <Line label={`${preview.total} produkter i filen`}>
                {preview.sheets.map((sheet) => `${sheet.name} → ${sheet.list}: ${sheet.products}`).join(' · ')}
              </Line>
              <Line label="Varugrupper">{preview.sheets.flatMap((sheet) => sheet.groups).join(', ') || 'Inga'}</Line>
              <Line label="Status">{preview.statuses.map((item) => `${item.label} ${item.count}`).join(' · ')}</Line>
              <Line label={`Byten av artikelnummer (${preview.replacements.length})`}>
                {preview.replacements.map((item) => `${item.from} → ${item.to}`).join(', ') || 'Inga'}
              </Line>
              <Line label={`Dubbletter (${preview.duplicates.length})`}>
                {preview.duplicates.map((item) => `${item.articleNumber} (${item.count})`).join(', ') || 'Inga'}
              </Line>
              <Line label={`Leverantörer som kan vara samma (${preview.supplierVariants.length})`}>
                {preview.supplierVariants.map((names) => names.join(' / ')).join(' · ') || 'Inga'}
              </Line>
              <Line label={`För långa fält (${preview.tooLong.count})`}>
                {preview.tooLong.samples.slice(0, 8).join(' · ') || 'Inga'}{preview.tooLong.count > 8 ? ' …' : ''}
              </Line>
              <Line label={`EAN med fel kontrollsiffra (${preview.invalidEan.count})`}>
                {preview.invalidEan.samples.slice(0, 8).join(' · ') || 'Inga'}
              </Line>
              {preview.unknownColumns.length > 0 && (
                <Line label="Kolumner utan eget fält (sparas under Övrigt)">{preview.unknownColumns.join(', ')}</Line>
              )}
              {preview.skippedRows.length > 0 && (
                <Line label={`Rader som hoppas över (${preview.skippedRows.length})`}>{preview.skippedRows.join(' · ')}</Line>
              )}
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Avbryt</Button>
        <Button variant="contained" disabled={busy || !preview || toAdd === 0} onClick={run}>
          {preview ? `Läs in ${toAdd} produkter` : 'Läs in'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImportDialog;
